import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { get, flow, find } from "lodash/fp";
import type { RootState } from "redux/store";
import { tripEmission, ElectricGridCountry } from "libraries/emission";
import { TripEmission, CarState, CarPaymentState } from "types/cars";
import { getLocalCars } from "hooks/cars/useGenericCars";
import { getNeutralCar } from "libraries/neutral";
import {
  MapExperience,
  EXPERIENCE_ID,
  MapLocation,
  MapCardMarker,
  MapIconMarker,
  MapItinerary,
  TravelMode,
  TripLegMode,
} from "types/map";
import { getNeutralTrip } from "libraries/trip";
import { getExperienceById, hasLatLong, neutralMapLocation } from "libraries/map";
import { padTripLegModes } from "libraries/tripLeg";
import { MyUserState, PaymentState, TripLocation, TripState } from "types/user";
import { shuffle, first, omit } from "lodash/fp";

/*
 * STORE TYPE
 */
type ShedState = {
  referenceItinerary: MapItinerary | null;
  referenceTrip: TripState;
  referenceCar: CarState;
  referenceExperience: MapExperience;
  referenceCardMarker: MapCardMarker | null;
  carHighlight: CarState | null;
  paymentsHighlight: Array<CarPaymentState> | null;
  // My user data
  userLoaded: boolean;
  userCars: CarState[];
  userPayments: PaymentState[];
  userTrips: TripState[];
  mapDrawerIsOpen: boolean;
  mapPositionTracker: boolean;
  referenceElectricGridCountry: ElectricGridCountry;
  referenceTripDestinations: MapLocation[];
  referenceTripLegModes: TripLegMode[];
};

const initialTripDestinations = (): MapLocation[] => [neutralMapLocation()];

const syncFinalDestinationToTrip = (state: ShedState) => {
  const last =
    state.referenceTripDestinations[state.referenceTripDestinations.length - 1];

  if (!last) {
    state.referenceTrip.toAddress = "";
    state.referenceTrip.toLatitude = 0;
    state.referenceTrip.toLongitude = 0;
    return;
  }

  state.referenceTrip.toAddress = last.formattedAddress ?? "";
  state.referenceTrip.toLatitude = last.latitude ?? 0;
  state.referenceTrip.toLongitude = last.longitude ?? 0;
};

const filledDestinationCount = (state: ShedState): number =>
  state.referenceTripDestinations.filter(hasLatLong).length;

const syncLegModesToDestinations = (state: ShedState) => {
  state.referenceTripLegModes = padTripLegModes(
    state.referenceTripLegModes,
    filledDestinationCount(state)
  );
};

const resetLegModesToDrive = (state: ShedState) => {
  state.referenceTripLegModes = padTripLegModes(
    [],
    filledDestinationCount(state)
  );
};

/*
 * STORE INITIAL STATE
 */
const initialState: ShedState = {
  referenceCar: flow(shuffle, first)(getLocalCars()) ?? getNeutralCar(),
  referenceItinerary: null,
  referenceTrip: getNeutralTrip(),
  referenceExperience: getExperienceById("shed-norway-all-partners"),
  carHighlight: null,
  paymentsHighlight: null,
  referenceCardMarker: null,
  userLoaded: false,
  userCars: [],
  userPayments: [],
  userTrips: [],
  mapDrawerIsOpen: false,
  mapPositionTracker: false,
  referenceElectricGridCountry: "norway",
  referenceTripDestinations: initialTripDestinations(),
  referenceTripLegModes: [],
};

/*
 * STORE UPDATE QUERIES
 */
export const shedSlice = createSlice({
  name: "shed",
  initialState,
  reducers: {
    loadUserHomeData: (
      state,
      { payload: { cars, offsets, trips } }: PayloadAction<MyUserState>
    ) => {
      state.userLoaded = true;
      state.userCars = cars;
      state.userPayments = offsets;
      state.userTrips = trips;
    },
    addUserCar: (state, { payload: car }: PayloadAction<CarState>) => {
      state.userCars.push(car);
    },
    addUserTrip: (state, { payload: trip }: PayloadAction<TripState>) => {
      state.userTrips = [trip, ...state.userTrips];
    },

    removeTrip: (state, { payload: tripId }: PayloadAction<string>) => {
      state.userTrips = state.userTrips.filter((x) => x.tripId !== tripId);
    },

    clearItinerary: (state) => {
      state.referenceItinerary = null;
      state.referenceTripDestinations = initialTripDestinations();
      state.referenceTripLegModes = [];
      state.referenceTrip = {
        ...getNeutralTrip(),
        carId: state.referenceTrip.carId,
        licensePlate: state.referenceTrip.licensePlate,
      };
    },

    clearTrip: (state) => {
      state.referenceTrip = getNeutralTrip();
      state.referenceTripDestinations = initialTripDestinations();
      state.referenceTripLegModes = [];
    },
    registerReferenceTripFrequency: (state, action: PayloadAction<number>) => {
      state.referenceTrip.commuteFrequency = action.payload;
    },
    registerReferenceTripSelection: (
      state,
      action: PayloadAction<TripState>
    ) => {
      state.referenceTrip = action.payload;
      state.referenceTripDestinations = [
        {
          formattedAddress: action.payload.toAddress,
          latitude: action.payload.toLatitude,
          longitude: action.payload.toLongitude,
        },
      ];
      syncLegModesToDestinations(state);
    },
    registerReferenceItinerary: (
      state,
      action: PayloadAction<MapItinerary>
    ) => {
      const next = action.payload;
      const current = state.referenceItinerary;

      if (
        current?.distanceMeters === next.distanceMeters &&
        current?.durationSeconds === next.durationSeconds &&
        (current?.flightDistanceMeters ?? 0) ===
          (next.flightDistanceMeters ?? 0) &&
        (current?.flightKgEmission ?? 0) === (next.flightKgEmission ?? 0) &&
        (current?.googleDirections?.length ?? 0) ===
          (next.googleDirections?.length ?? 0) &&
        (current?.flightHops?.length ?? 0) === (next.flightHops?.length ?? 0) &&
        (current?.walkDistanceMeters ?? 0) ===
          (next.walkDistanceMeters ?? 0) &&
        (current?.cycleDistanceMeters ?? 0) ===
          (next.cycleDistanceMeters ?? 0) &&
        (current?.trainDistanceMeters ?? 0) ===
          (next.trainDistanceMeters ?? 0) &&
        (current?.trainKgEmission ?? 0) === (next.trainKgEmission ?? 0) &&
        (current?.walkBikeAvoidedKgEmission ?? 0) ===
          (next.walkBikeAvoidedKgEmission ?? 0)
      ) {
        return;
      }

      state.referenceItinerary = next;
    },
    registerReferenceTripTravelMode: (
      state,
      action: PayloadAction<TravelMode>
    ) => {
      if (
        [
          TravelMode.DRIVING,
          TravelMode.BICYCLING,
          TravelMode.DIGITAL,
          TravelMode.WALKING,
        ].includes(action.payload)
      ) {
        state.referenceTrip.mapTravelMode = action.payload;
      } else {
        console.warn("[Map] Register Travel mode incompatible", action);
      }
    },

    updateMyUserTrips: (
      state,
      {
        payload: {
          tripId,
          fromLatitude,
          fromLongitude,
          toLatitude,
          toLongitude,
          fromAddress,
          toAddress,
          commuteFrequency,
        },
      }
    ) => {
      const trip = state.userTrips.find((trip) => trip.tripId === tripId);

      if (trip) {
        trip.fromAddress = fromAddress;
        trip.fromLatitude = fromLatitude;
        trip.fromLongitude = fromLongitude;
        trip.toAddress = toAddress;
        trip.toLatitude = toLatitude;
        trip.toLongitude = toLongitude;
        trip.commuteFrequency = commuteFrequency;
      }
    },
    registerReferenceExperienceId: (
      state,
      { payload: experienceId }: PayloadAction<EXPERIENCE_ID>
    ) => {
      const newReferenceExperience = getExperienceById(experienceId);

      if (newReferenceExperience) {
        state.referenceExperience = newReferenceExperience;
      }
    },
    registerNewTripFromMarker: (
      state,
      { payload: baseMarker }: PayloadAction<MapIconMarker | MapCardMarker>
    ) => {
      state.mapPositionTracker = true;

      const markerLocation: MapLocation = {
        formattedAddress: baseMarker.name,
        latitude: baseMarker.lat,
        longitude: baseMarker.lng,
      };

      state.referenceTripDestinations = [markerLocation];
      syncFinalDestinationToTrip(state);
      syncLegModesToDestinations(state);
    },
    registerReferenceCardMarker: (
      state,
      { payload: cardMarker }: PayloadAction<MapCardMarker>
    ) => {
      state.mapDrawerIsOpen = true;
      state.referenceCardMarker = cardMarker;
    },
    registerInitialTripCurrentLocation: (
      state,
      { payload: fromLocation }: PayloadAction<MapLocation>
    ) => {
      // Prevent Overiding if existing from location
      if (
        !state.referenceTrip.fromAddress &&
        !state.referenceTrip.fromLatitude &&
        !state.referenceTrip.fromLongitude
      ) {
        state.referenceTrip.fromAddress = fromLocation.formattedAddress ?? "";
        state.referenceTrip.fromLatitude = fromLocation.latitude ?? 0;
        state.referenceTrip.fromLongitude = fromLocation.longitude ?? 0;
      }
    },
    registerReferenceTripLocation: (
      state,
      { payload: tripLocation }: PayloadAction<Partial<TripLocation> | null>
    ) => {
      const {
        fromLatitude,
        fromLongitude,
        toLatitude,
        toLongitude,
        fromAddress,
        toAddress,
      } = tripLocation || {};

      if (fromLatitude && fromLongitude) {
        state.referenceTrip.fromAddress = fromAddress ?? "";
        state.referenceTrip.fromLatitude = fromLatitude;
        state.referenceTrip.fromLongitude = fromLongitude;
      }

      if (toLatitude && toLongitude) {
        state.referenceTrip.toAddress = toAddress ?? "";
        state.referenceTrip.toLatitude = toLatitude;
        state.referenceTrip.toLongitude = toLongitude;
        state.mapPositionTracker = true;

        const lastIndex = state.referenceTripDestinations.length - 1;
        state.referenceTripDestinations[lastIndex] = {
          formattedAddress: toAddress ?? "",
          latitude: toLatitude,
          longitude: toLongitude,
        };
        syncLegModesToDestinations(state);
      }
    },
    registerReferenceTripFrom: (
      state,
      { payload: fromLocation }: PayloadAction<MapLocation>
    ) => {
      state.referenceTrip.fromAddress = fromLocation.formattedAddress ?? "";
      state.referenceTrip.fromLatitude = fromLocation.latitude ?? 0;
      state.referenceTrip.fromLongitude = fromLocation.longitude ?? 0;
      state.referenceTrip.fromPlaceName = fromLocation.placeName;
      state.referenceTrip.fromPlaceTypes = fromLocation.placeTypes;
    },
    registerReferenceTripDestination: (
      state,
      {
        payload: { index, location },
      }: PayloadAction<{ index: number; location: MapLocation }>
    ) => {
      if (index >= 0 && index < state.referenceTripDestinations.length) {
        state.referenceTripDestinations[index] = location;
        syncFinalDestinationToTrip(state);
        syncLegModesToDestinations(state);
        if (location.latitude && location.longitude) {
          state.mapPositionTracker = true;
        }
      }
    },
    addReferenceTripDestination: (state) => {
      const MAX_DESTINATIONS = 26;

      if (state.referenceTripDestinations.length < MAX_DESTINATIONS) {
        state.referenceTripDestinations.push(neutralMapLocation());
      }
    },
    appendReferenceTripDestination: (
      state,
      { payload: location }: PayloadAction<MapLocation>
    ) => {
      const MAX_DESTINATIONS = 26;

      if (state.referenceTripDestinations.length < MAX_DESTINATIONS) {
        state.referenceTripDestinations.push(location);
        syncFinalDestinationToTrip(state);
        syncLegModesToDestinations(state);
        if (location.latitude && location.longitude) {
          state.mapPositionTracker = true;
        }
      }
    },
    removeReferenceTripDestination: (
      state,
      { payload: index }: PayloadAction<number>
    ) => {
      if (
        state.referenceTripDestinations.length > 1 &&
        index >= 0 &&
        index < state.referenceTripDestinations.length
      ) {
        state.referenceTripDestinations.splice(index, 1);
        if (index < state.referenceTripLegModes.length) {
          state.referenceTripLegModes.splice(index, 1);
        }
        syncFinalDestinationToTrip(state);
        syncLegModesToDestinations(state);
      }
    },
    setReferenceTripDestinations: (
      state,
      { payload: destinations }: PayloadAction<MapLocation[]>
    ) => {
      state.referenceTripDestinations =
        destinations.length > 0 ? destinations : initialTripDestinations();
      syncFinalDestinationToTrip(state);
      resetLegModesToDrive(state);
    },
    setReferenceTripLegModes: (
      state,
      { payload: modes }: PayloadAction<TripLegMode[]>
    ) => {
      state.referenceTripLegModes = padTripLegModes(
        modes,
        filledDestinationCount(state)
      );
    },
    setReferenceTripLegMode: (
      state,
      {
        payload: { index, mode },
      }: PayloadAction<{ index: number; mode: TripLegMode }>
    ) => {
      syncLegModesToDestinations(state);
      if (index >= 0 && index < state.referenceTripLegModes.length) {
        state.referenceTripLegModes[index] = mode;
      }
    },
    registerReferenceElectricGridCountry: (
      state,
      { payload: gridCountry }: PayloadAction<ElectricGridCountry>
    ) => {
      state.referenceElectricGridCountry = gridCountry;
    },
    registerReferenceCar: (state, action: PayloadAction<CarState>) => {
      state.referenceCar = action.payload;
    },
    highlightCar: (state, action: PayloadAction<CarState>) => {
      state.carHighlight = action.payload;
    },
    highlightPayments: (state, action: PayloadAction<CarPaymentState[]>) => {
      state.paymentsHighlight = action.payload;
    },

    mapOpenDrawer: (state) => {
      state.mapDrawerIsOpen = true;
    },
    mapCloseDrawer: (state) => {
      state.mapDrawerIsOpen = false;
    },
  },
});

/*
 * STORE SELECT QUERIES
 */

export const selectIsUserLoaded = (state: RootState): boolean =>
  state.shed.userLoaded;

export const selectMyCars = (state: RootState): CarState[] =>
  state.shed.userCars;

export const selectMyPayments = (state: RootState): PaymentState[] =>
  state.shed.userPayments;

export const selectMyTrips = (state: RootState): TripState[] =>
  state.shed.userTrips;

export const selectTripTravelMode = (state: RootState): TravelMode =>
  state.shed.referenceTrip.mapTravelMode;

export const selectHasReferenceTrip = (state: RootState): boolean =>
  Boolean(state.shed.referenceItinerary && state.shed.referenceCar);

export const selectMapDrawerIsOpen = (state: RootState): Boolean =>
  state.shed.mapDrawerIsOpen;

export const selectMapPositionTracker = (state: RootState): Boolean =>
  state.shed.mapPositionTracker;

export const selectReferenceCardMarker = (
  state: RootState
): MapCardMarker | null => state.shed.referenceCardMarker;

export const selectReferenceTrip = (state: RootState): TripState =>
  state.shed.referenceTrip;

export const selectReferenceCar = (state: RootState): CarState | null =>
  state.shed.referenceCar;

export const selectReferenceItinerary = (
  state: RootState
): MapItinerary | null => state.shed.referenceItinerary;

export const selectReferenceExperience = (state: RootState): MapExperience =>
  state.shed.referenceExperience;

export const selectReferenceExperienceId = (state: RootState): EXPERIENCE_ID =>
  state.shed.referenceExperience?.experienceId ?? "shed-norway-all-partners";

export const selectReferenceTripDestinations = (
  state: RootState
): MapLocation[] => state.shed.referenceTripDestinations;

export const selectReferenceTripLegModes = (state: RootState): TripLegMode[] =>
  state.shed.referenceTripLegModes;

export const selectReferenceTripFrom = createSelector(
  [
    (state: RootState) => state.shed.referenceTrip.fromAddress,
    (state: RootState) => state.shed.referenceTrip.fromLatitude,
    (state: RootState) => state.shed.referenceTrip.fromLongitude,
    (state: RootState) => state.shed.referenceTrip.fromPlaceName,
    (state: RootState) => state.shed.referenceTrip.fromPlaceTypes,
  ],
  (
    fromAddress,
    fromLatitude,
    fromLongitude,
    fromPlaceName,
    fromPlaceTypes
  ): MapLocation => ({
    formattedAddress: fromAddress,
    latitude: fromLatitude,
    longitude: fromLongitude,
    placeName: fromPlaceName,
    placeTypes: fromPlaceTypes,
  })
);

export const selectReferenceElectricGridCountry = (
  state: RootState
): ElectricGridCountry => state.shed.referenceElectricGridCountry;

export const selectReferenceTripEmission = (state: RootState): TripEmission =>
  tripEmission(
    state.shed.referenceCar,
    state.shed.referenceItinerary?.distanceMeters ?? 0,
    { gridCountry: state.shed.referenceElectricGridCountry }
  );

export const selectCarHighlight = (state: RootState): CarState | null =>
  state.shed.carHighlight;

export const selectPaymentsHighlight = (
  state: RootState
): Array<CarPaymentState> | null => state.shed.paymentsHighlight;

export const selectCarByLicensePlate = (licensePlate: string) =>
  flow(
    get("shed.userCars"),
    find({ licensePlate: licensePlate }),
    (car: CarState | undefined) => omit(["__typename"])(car) ?? null // Type inference
  );

export const selectCarByGenericVin = (genericVin: string) =>
  flow(
    get("shed.userCars"),
    find({ genericVin: genericVin }),
    (car: CarState | undefined) => omit(["__typename"])(car) ?? null // Type inference
  );

/*
 * STORE EXPORT DEFAULT
 */
export const {
  registerReferenceItinerary,
  clearItinerary,
  clearTrip,
  loadUserHomeData,
  addUserCar,
  addUserTrip,
  removeTrip,
  registerReferenceExperienceId,
  registerReferenceElectricGridCountry,
  registerReferenceCar,
  registerReferenceTripSelection,
  registerNewTripFromMarker,
  registerReferenceCardMarker,
  registerInitialTripCurrentLocation,
  registerReferenceTripLocation,
  registerReferenceTripFrom,
  registerReferenceTripDestination,
  addReferenceTripDestination,
  appendReferenceTripDestination,
  removeReferenceTripDestination,
  setReferenceTripDestinations,
  setReferenceTripLegModes,
  setReferenceTripLegMode,
  registerReferenceTripFrequency,
  registerReferenceTripTravelMode,
  mapOpenDrawer,
  mapCloseDrawer,
  updateMyUserTrips,
  highlightCar,
  highlightPayments,
} = shedSlice.actions;
export default shedSlice.reducer;
